<template>
  <div class="entry-content">
    <div class="row">
      <div class="col-md-12">
        <div class="entry-header-admin">
          <div class="gap-40"></div>
          <div class="col-md-12 entry-title-admin">
            <div class="row">
              <div class="col-md-8">
                <p class="font-120P">Agent License Details</p>
              </div>
              <div class="col-md-4 text-right">
                <router-link
                  :to="{ name: 'agent_licenses' }"
                  class="btn btn-secondary"
                >
                  <i class="fa fa-arrow-left"></i> Back to List
                </router-link>
                <router-link
                  :to="{ name: 'agent_licenses_edit', params: { id: $route.params.id } }"
                  class="btn btn-primary ml-2"
                >
                  <i class="fa fa-edit"></i> Edit
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="gap-40"></div>

    <!-- Loading Spinner -->
    <div v-if="isLoading" class="text-center">
      <i class="fa fa-spinner fa-spin fa-3x"></i>
      <p>Loading agent license...</p>
    </div>

    <!-- Agent License Details -->
    <div v-else class="row">
      <div class="col-md-12">
        <div class="panel panel-default">
          <div class="panel-heading">
            <h3 class="panel-title">License Information</h3>
          </div>
          <div class="panel-body">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="listing-label">Agent Name</label>
                  <p class="listing-value">{{ agentLicense.name }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="listing-label">License Number</label>
                  <p class="listing-value">{{ agentLicense.line_one }}</p>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="listing-label">Path</label>
                  <p class="listing-value">
                    <a v-if="agentLicense.path" :href="agentLicense.path" target="_blank">
                      {{ agentLicense.path }}
                    </a>
                    <span v-else class="text-muted">-</span>
                  </p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="listing-label">Base</label>
                  <p class="listing-value">{{ agentLicense.base || '-' }}</p>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="listing-label">Status</label>
                  <p class="listing-value">
                    <span 
                      class="badge"
                      :class="{
                        'badge-success': agentLicense.status === 'active',
                        'badge-secondary': agentLicense.status === 'inactive'
                      }"
                    >
                      {{ agentLicense.status || 'active' }}
                    </span>
                  </p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="listing-label">Created Date</label>
                  <p class="listing-value">{{ formatDate(agentLicense.created_at) }}</p>
                </div>
              </div>
            </div>

            <div class="row" v-if="agentLicense.description">
              <div class="col-md-12">
                <div class="form-group">
                  <label class="listing-label">Description</label>
                  <p class="listing-value">{{ agentLicense.description }}</p>
                </div>
              </div>
            </div>

            <div class="row" v-if="agentLicense.updated_at">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="listing-label">Last Updated</label>
                  <p class="listing-value">{{ formatDate(agentLicense.updated_at) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="row" v-if="!isLoading">
      <div class="col-md-12">
        <div class="text-center">
          <router-link
            :to="{ name: 'agent_licenses_edit', params: { id: $route.params.id } }"
            class="btn btn-primary"
          >
            <i class="fa fa-edit"></i> Edit License
          </router-link>
          <button
            @click="deleteLicense"
            class="btn btn-danger ml-2"
          >
            <i class="fa fa-trash"></i> Delete License
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { configs } from "@/lib/axios";
export default {
  name: 'AgentLicenseShow',
  data() {
    return {
      agentLicense: {},
      isLoading: true
    };
  },
  mounted() {
    this.setupBreadcrumbs();
    this.loadAgentLicense();
  },
  methods: {
    setupBreadcrumbs() {
      this.$store.commit("app/SET_BREADCRUMBS", [
        {
          name: "Dashboard",
          url: "home"
        },
        {
          name: "Admin",
          url: "admin_users"
        },
        {
          name: "Agent Licenses",
          url: "agent_licenses"
        },
        {
          name: "Details",
          url: null,
          active: true
        }
      ]);
    },
    
    async loadAgentLicense() {
      try {
        const response = await configs.get(
          `/admin/agent-licenses/${this.$route.params.id}`
        );
        
        this.agentLicense = response.data;
      } catch (error) {
        console.error('Error loading agent license:', error);
        this.$toast.error('Error loading agent license');
        this.$router.push({ name: 'agent_licenses' });
      } finally {
        this.isLoading = false;
      }
    },
    
    async deleteLicense() {
      if (!confirm('Are you sure you want to delete this agent license?')) {
        return;
      }
      
      try {
        await configs.delete(`/admin/agent-licenses/${this.$route.params.id}`);
        
        this.$toast.success('Agent license deleted successfully');
        this.$router.push({ name: 'agent_licenses' });
      } catch (error) {
        console.error('Error deleting agent license:', error);
        this.$toast.error('Error deleting agent license');
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.entry-header-admin {
  background-color: #f8f9fa;
  border-radius: 5px;
  padding: 20px;
}

.entry-title-admin {
  margin-bottom: 0;
}

.panel {
  border: 1px solid #ddd;
  border-radius: 5px;
}

.panel-heading {
  background-color: #f8f9fa;
  border-bottom: 1px solid #ddd;
  padding: 10px 15px;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
}

.panel-body {
  padding: 15px;
}

.listing-label {
  font-weight: bold;
  color: #666;
  margin-bottom: 5px;
}

.listing-value {
  font-size: 14px;
  margin-bottom: 15px;
}

.badge-success {
  background-color: #28a745;
}

.badge-secondary {
  background-color: #6c757d;
}

.ml-2 {
  margin-left: 10px;
}
</style> 